import type { AriaRole } from 'react'
import { mfLogoBaseUrl } from '../../env'
import { roundOffAndFormatNumberWithCurrency, toPascalCaseWithSpaces } from '../../utils/utils'
import { PowerFormBadge } from '../ui-components/PowerFormBadge'
import { RegularTag } from '../ui-components/RegularTag'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { getPowerFormLabel } from '../../utils/formMetaData'

/** WebKit role for single AXStaticText focus stop (TalkBack/VoiceOver grouping). */
export const GROUPED_STATIC_TEXT_ROLE = 'text' as AriaRole

export const KnowWhyInvestment = ({
  logoSrc,
  title,
  fundType,
  fundCategory,
  investedAmount,
  currentGain,
  powerForm,
  isRegular
}: {
  logoSrc: string
  title: string
  fundType: string
  fundCategory: string
  investedAmount?: number
  currentGain?: number
  powerForm: `${PowerPerformanceForms}` | null
  isRegular: boolean
}) => {
  const typeCategory = `${toPascalCaseWithSpaces(fundType)} / ${toPascalCaseWithSpaces(fundCategory)}`
  const currentLabel =
    currentGain != null ? roundOffAndFormatNumberWithCurrency(currentGain) : undefined
  const investedLabel =
    investedAmount != null && investedAmount > 0
      ? roundOffAndFormatNumberWithCurrency(investedAmount)
      : undefined
  const formLabel = powerForm ? getPowerFormLabel(powerForm) : ''

  const accessibleName = [
    title,
    typeCategory,
    currentLabel && `Current value, ${currentLabel}`,
    investedLabel && `Invested, ${investedLabel}`,
    formLabel,
    isRegular ? 'Regular plan' : null
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <div
      role={GROUPED_STATIC_TEXT_ROLE}
      aria-label={accessibleName}
      className='flex flex-col w-full pt-3 pb-[11px] px-4 border-[1.5px] border-[#F0F4F7] rounded-xl'
    >
      <div aria-hidden='true' className='flex flex-col w-full'>
        <div className='flex justify-between'>
          <div className='flex gap-4 flex-1 min-w-0'>
            <div className='h-10 w-10 flex-shrink-0'>
              <div className='h-10 w-10'>
                <img
                  className='h-full w-full object-contain'
                  src={`${mfLogoBaseUrl}${logoSrc}`}
                  alt=''
                />
              </div>
            </div>
            <div className='flex flex-col min-w-0 flex-1'>
              <div className='text-[13px] text-black leading-[140%] font-[300] truncate' title={title}>
                {title}
              </div>
              <div className='pt-[2px] text-[13px] text-[#6A6E6E]'>{typeCategory}</div>
            </div>
          </div>
          <div className='flex-shrink-0 ml-4'>
            {investedAmount ? (
              <div className='flex flex-col'>
                <div className='text-[13px] text-black font-[500] text-right'>{currentLabel}</div>
                <div className='pt-[1px] text-[13px] text-[#6A6E6E] text-right'>{investedLabel}</div>
              </div>
            ) : null}
          </div>
        </div>
        <div className='flex items-center gap-2 pt-4 pl-[54px]'>
          <PowerFormBadge powerForm={powerForm} />
          {isRegular && <RegularTag />}
        </div>
      </div>
    </div>
  )
}
